import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Typography } from '@/components/ui';
import { cn } from '@/lib/utils';
import { errorReporting } from '@/services/errorReporting';
import { GlobalErrorPage } from '@/pages/ErrorPages/GlobalErrorPage';

export interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  level?: 'page' | 'component' | 'global';
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number>;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  private resetTimeoutId: number | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Report to error monitoring service
    errorReporting.captureReactError(error, errorInfo, {
      tags: {
        errorBoundaryLevel: this.props.level || 'component',
      },
      extra: {
        props: this.props,
      },
    });

    // Call custom error handler
    this.props.onError?.(error, errorInfo);
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetOnPropsChange, resetKeys } = this.props;
    const { hasError } = this.state;

    // Reset error state when resetKeys change
    if (hasError && resetKeys && prevProps.resetKeys) {
      const hasResetKeyChanged = resetKeys.some(
        (key, index) => key !== prevProps.resetKeys?.[index]
      );

      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
      }
    }

    // Reset error state when props change (if enabled)
    if (
      hasError &&
      resetOnPropsChange &&
      prevProps.children !== this.props.children
    ) {
      this.resetErrorBoundary();
    }
  }

  resetErrorBoundary = () => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }

    this.resetTimeoutId = window.setTimeout(() => {
      this.setState({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
      });
    }, 0);
  };

  override render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback based on error level
      return this.renderDefaultFallback();
    }

    return this.props.children;
  }

  private renderDefaultFallback() {
    const { level = 'component' } = this.props;
    const { error } = this.state;

    const isDev = import.meta.env.DEV;

    switch (level) {
      case 'global':
        return (
          <GlobalErrorPage
            error={error}
            onRetry={() => window.location.reload()}
            onReset={this.resetErrorBoundary}
          />
        );

      case 'page':
        return (
          <div className="min-h-[400px] flex items-center justify-center p-6">
            <div className="max-w-sm w-full text-center">
              <div className="w-12 h-12 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-error-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>

              <Typography
                variant="body-black-center"
                className="font-medium mb-2"
              >
                Page Error
              </Typography>
              <Typography variant="body-gray-center" className="text-sm mb-4">
                This page encountered an error. Please try again.
              </Typography>

              <Button
                onClick={this.resetErrorBoundary}
                variant="primary"
                size="sm"
              >
                Try Again
              </Button>

              {isDev && (
                <details className="mt-4 text-left">
                  <summary className="text-xs text-gray-400 cursor-pointer">
                    Debug Info
                  </summary>
                  <pre className="mt-2 text-xs bg-gray-50 p-2 rounded overflow-auto">
                    {error?.message}
                  </pre>
                </details>
              )}
            </div>
          </div>
        );

      case 'component':
      default:
        return (
          <div
            className={cn(
              'border border-error-200 bg-error-50 rounded-lg p-4',
              'flex items-center space-x-3'
            )}
          >
            <div className="flex-shrink-0">
              <svg
                className="w-5 h-5 text-error-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>

            <div className="flex-1 min-w-0">
              <Typography
                variant="body-primary"
                className="text-sm font-medium"
              >
                Component Error
              </Typography>
              <Typography variant="body-gray-2" className="text-xs">
                Something went wrong with this component.
              </Typography>
            </div>

            <Button
              onClick={this.resetErrorBoundary}
              variant="text"
              size="sm"
              className="flex-shrink-0 text-xs text-error-600 hover:text-error-700"
            >
              Retry
            </Button>
          </div>
        );
    }
  }
}
